import React from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import './App.css';

// Pages
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import DashboardPage from './pages/DashboardPage';
import WhatsAppPage from './pages/WhatsAppPage';
import ContactsPage from './pages/ContactsPage';
import CampaignsPage from './pages/CampaignsPage';
import GroupsPage from './pages/GroupsPage';
import UsersPage from './pages/UsersPage';
import MessagesPage from './pages/MessagesPage';
import SendMessagePage from './pages/SendMessagePage';
import AdminsPage from './pages/AdminsPage';
import PatRepositoryPage from './pages/PatRepositoryPage';
import BlockedAccountsPage from './pages/BlockedAccountsPage';
import SmtpPage from './pages/SmtpPage';
import CmsPage from './pages/CmsPage';
import PurgePage from './pages/PurgePage';
import StripePage from './pages/StripePage';
import BillingTestsPage from './pages/BillingTestsPage';
import InfoSystemPage from './pages/InfoSystemPage';
import MaintenancePage from './pages/MaintenancePage';
import MessagesLivePage from './pages/MessagesLivePage';
import CelularesEmUsoPage from './pages/CelularesEmUsoPage';
import SubscriptionPage from './pages/SubscriptionPage';
import SignupPage from './pages/SignupPage';
import SignupEmailPage from './pages/SignupEmailPage';
import SignupCodePage from './pages/SignupCodePage';
import ConfirmEmailPage from './pages/ConfirmEmailPage';
import CheckoutSuccessPage from './pages/CheckoutSuccessPage';
import SetPasswordPage from './pages/SetPasswordPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import MyDataPage from './pages/MyDataPage';
import Layout from './components/Layout';
import TrialGuard from './components/TrialGuard';
import CookieBanner from './components/CookieBanner';

// Componente para rota pública (redireciona usuários logados)
function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Carregando...</div>;
  }
  
  if (user) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
}

function ProtectedRoute({ children, allowedRoles, ownerOnly }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/" replace />;
  }

  // Páginas exclusivas do dono do tenant (ex.: Minha Assinatura).
  // Sub-usuários, mesmo com perfil ADMIN, não acessam — nem pela URL.
  if (ownerOnly && !user.is_tenant_owner) {
    return <Navigate to="/" replace />;
  }

  return <TrialGuard>{children}</TrialGuard>;
}

function AppRoutes() {
  const { user } = useAuth();

  // Default route based on role
  const getDefaultRoute = () => {
    if (!user) return '/';
    if (user.role === 'SUPER_ADMIN') return '/admins';
    return '/dashboard';
  };

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={
        <PublicRoute>
          <LandingPage />
        </PublicRoute>
      } />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/signup/email" element={<SignupEmailPage />} />
      <Route path="/signup/code" element={<SignupCodePage />} />
      <Route path="/signup/success" element={<SignupPage />} />
      <Route path="/signup/cancel" element={<SignupPage />} />
      <Route path="/signup/checkout-success" element={<CheckoutSuccessPage />} />
      <Route path="/set-password" element={<SetPasswordPage />} />
      <Route path="/confirm-email" element={<ConfirmEmailPage />} />
      <Route path="/politica-de-privacidade" element={<PrivacyPolicyPage />} />
      <Route path="/termos-de-uso" element={<TermsOfServicePage />} />
      
      {/* Super Admin Routes */}
      <Route
        path="/admins"
        element={
          <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
            <Layout>
              <AdminsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/pat-repository"
        element={
          <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
            <Layout>
              <PatRepositoryPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/blocked-accounts"
        element={
          <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
            <Layout>
              <BlockedAccountsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/celulares-em-uso"
        element={
          <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
            <Layout>
              <CelularesEmUsoPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/smtp"
        element={
          <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
            <Layout>
              <SmtpPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/cms"
        element={
          <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
            <Layout>
              <CmsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/purge"
        element={
          <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
            <Layout>
              <PurgePage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/stripe"
        element={
          <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
            <Layout>
              <StripePage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/billing-tests"
        element={
          <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
            <Layout>
              <BillingTestsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/info-system"
        element={
          <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
            <Layout>
              <InfoSystemPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/maintenance"
        element={
          <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
            <Layout>
              <MaintenancePage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/mensagens-live"
        element={
          <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
            <Layout>
              <MessagesLivePage />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Admin Routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute allowedRoles={['ADMIN', 'USER_EDITOR', 'USER_SEND', 'USER_READ']}>
            <Layout>
              <DashboardPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/whatsapp"
        element={
          <ProtectedRoute allowedRoles={['ADMIN']}>
            <Layout>
              <WhatsAppPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/contacts"
        element={
          <ProtectedRoute allowedRoles={['ADMIN', 'USER_EDITOR', 'USER_SEND', 'USER_READ']}>
            <Layout>
              <ContactsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/campaigns"
        element={
          <ProtectedRoute allowedRoles={['ADMIN', 'USER_EDITOR', 'USER_SEND', 'USER_READ']}>
            <Layout>
              <CampaignsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/users"
        element={
          <ProtectedRoute allowedRoles={['ADMIN']}>
            <Layout>
              <UsersPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/messages"
        element={
          <ProtectedRoute allowedRoles={['ADMIN', 'USER_EDITOR', 'USER_SEND', 'USER_READ']}>
            <Layout>
              <MessagesPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/send"
        element={
          <ProtectedRoute allowedRoles={['ADMIN', 'USER_EDITOR', 'USER_SEND']}>
            <Layout>
              <SendMessagePage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/groups"
        element={
          <ProtectedRoute allowedRoles={['ADMIN', 'USER_EDITOR']}>
            <Layout>
              <GroupsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/subscription"
        element={
          <ProtectedRoute allowedRoles={['ADMIN']} ownerOnly>
            <Layout>
              <SubscriptionPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/meus-dados"
        element={
          <ProtectedRoute allowedRoles={['ADMIN']} ownerOnly>
            <Layout>
              <MyDataPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Default redirect */}
      <Route path="/" element={<Navigate to={getDefaultRoute()} replace />} />
      <Route path="*" element={<Navigate to={getDefaultRoute()} replace />} />
    </Routes>
  );
}

function App() {
  return (
    <HelmetProvider>
      <ThemeProvider>
        <AuthProvider>
          <Router>
            <AppRoutes />
            <CookieBanner />
            <Toaster
              position="top-right"
              richColors
              closeButton
              duration={3000}
              toastOptions={{
                style: {
                  minHeight: '60px',
                },
              }}
            />
          </Router>
        </AuthProvider>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
